// src/pages/SupplyChain.jsx
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search as SearchIcon,
  Columns3,
  ListFilter,
  Download,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  TrendingDown,
  TrendingUp,
} from "lucide-react";
import Sidebar from "../components/Sidebar";
import { useSidebarCollapsed } from "../utils/useSidebarCollapsed";
import { FLAG, loadCompanies } from "../data/supplyChainCompanies";

const panel = "rounded-md border border-sky-900/30 bg-[#0a1224]/70";

const primaryBtn =
  "inline-flex items-center gap-1.5 rounded-md bg-sky-600 hover:bg-sky-500 px-3.5 py-1.5 text-[13px] font-semibold text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/40 transition-colors";

const ghostBtn =
  "inline-flex items-center gap-1.5 rounded-md border border-sky-900/30 bg-transparent hover:bg-sky-900/30 px-2.5 py-1.5 text-[12px] font-medium text-neutral-300 hover:text-sky-300 transition-colors";

// ── Helpers ────────────────────────────────────────────────────────────────
function scoreTone(score) {
  if (score >= 75) return { ring: "#f43f5e", text: "text-rose-300" };       // critical
  if (score >= 50) return { ring: "#f59e0b", text: "text-amber-300" };       // elevated
  if (score >= 25) return { ring: "#eab308", text: "text-yellow-300" };      // moderate
  return { ring: "#10b981", text: "text-emerald-300" };                      // low
}

function Gauge({ score }) {
  const { ring, text } = scoreTone(score);
  const r = 14;
  const c = 2 * Math.PI * r;
  const offset = c - (Math.max(0, Math.min(100, score)) / 100) * c;
  return (
    <div className="relative h-9 w-9 shrink-0">
      <svg viewBox="0 0 36 36" className="h-9 w-9 -rotate-90">
        <circle cx="18" cy="18" r={r} fill="none" stroke="rgba(148,163,184,0.18)" strokeWidth="3" />
        <circle
          cx="18"
          cy="18"
          r={r}
          fill="none"
          stroke={ring}
          strokeWidth="3"
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={offset}
        />
      </svg>
      <div className={`absolute inset-0 flex items-center justify-center text-[11px] font-semibold ${text}`}>
        {score}
      </div>
    </div>
  );
}

function CompanyAvatar({ name }) {
  // Deterministic pastel from name hash → keeps avatar stable per row.
  const safeName = String(name || "");
  const hash = Array.from(safeName).reduce((a, c) => a + c.charCodeAt(0), 0);
  const palette = [
    "from-sky-500/30 to-sky-700/30 text-sky-200 border-sky-700/40",
    "from-violet-500/30 to-violet-700/30 text-violet-200 border-violet-700/40",
    "from-emerald-500/30 to-emerald-700/30 text-emerald-200 border-emerald-700/40",
    "from-rose-500/30 to-rose-700/30 text-rose-200 border-rose-700/40",
    "from-amber-500/30 to-amber-700/30 text-amber-200 border-amber-700/40",
    "from-cyan-500/30 to-cyan-700/30 text-cyan-200 border-cyan-700/40",
  ];
  const cls = palette[hash % palette.length];
  const initial = safeName.replace(/[^A-Za-z]/g, "").slice(0, 1).toUpperCase() || "?";
  return (
    <div
      className={`h-8 w-8 shrink-0 rounded-full border bg-gradient-to-br ${cls} flex items-center justify-center text-[12px] font-semibold`}
    >
      {initial}
    </div>
  );
}

function ScoreCell({ v, d }) {
  return (
    <div className="leading-tight">
      <div className="text-[13px] text-neutral-200 tabular-nums">{v}</div>
      <div className="text-[11px] text-neutral-500 tabular-nums mt-0.5">{d}</div>
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────
export default function SupplyChain() {
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useSidebarCollapsed();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [sortKey, setSortKey] = useState("identity");
  const [sortDir, setSortDir] = useState("desc"); // 'asc' | 'desc'
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [companies, setCompanies] = useState(() => loadCompanies());

  // Re-load when window regains focus (e.g. user added a company on the
  // manage page and returned to this one).
  useEffect(() => {
    const onFocus = () => setCompanies(loadCompanies());
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let rows = companies;
    if (q) {
      rows = rows.filter(
        (r) =>
          r.name.toLowerCase().includes(q) ||
          r.domain.toLowerCase().includes(q)
      );
    }
    const accessor = {
      name: (r) => r.name.toLowerCase(),
      sighting: (r) => r.sighting,
      identity: (r) => r.identity,
      malware: (r) => r.malware.v,
      breach: (r) => r.breach.v,
      phish: (r) => r.phish.v,
      combo: (r) => r.combo.v,
      size: (r) => r.size,
      country: (r) => r.country,
    }[sortKey];
    const sorted = [...rows].sort((a, b) => {
      const av = accessor(a);
      const bv = accessor(b);
      if (av < bv) return sortDir === "asc" ? -1 : 1;
      if (av > bv) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [query, sortKey, sortDir, companies]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const pageRows = filtered.slice((page - 1) * pageSize, page * pageSize);

  const onSort = (key) => {
    if (key === sortKey) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("desc");
    }
  };

  const SortHeader = ({ k, children, className = "" }) => {
    const active = sortKey === k;
    return (
      <th
        scope="col"
        className={`px-4 py-2.5 text-left text-[11px] uppercase tracking-wider font-medium text-neutral-400 select-none cursor-pointer hover:text-neutral-200 ${className}`}
        onClick={() => onSort(k)}
      >
        <span className="inline-flex items-center gap-1">
          {children}
          {active &&
            (sortDir === "asc" ? (
              <ChevronUp className="h-3 w-3" />
            ) : (
              <ChevronDown className="h-3 w-3" />
            ))}
        </span>
      </th>
    );
  };

  return (
    <div className="min-h-screen bg-[#000414] text-white">
      <header
        className="bg-[#0a1224]/85 border-b border-sky-800/40 backdrop-blur sticky top-0 z-40 shadow-[0_6px_16px_-8px_rgba(0,0,0,0.7)]"
        style={{ marginLeft: collapsed ? "56px" : "224px" }}
      >
        <div className="px-6 h-14 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <h1 className="text-[20px] font-bold leading-tight tracking-tight">
              Supply Chain Threat Protection
            </h1>
          </div>
          <button
            type="button"
            onClick={() => navigate("/supply-chain/manage")}
            className={primaryBtn}
          >
            Manage Companies
          </button>
        </div>
      </header>

      <div className="flex">
        <Sidebar
          collapsed={collapsed}
          setCollapsed={setCollapsed}
          mobileOpen={mobileOpen}
          setMobileOpen={setMobileOpen}
          activeKey="supplychain"
          onChange={() => {}}
        />

        <main className="relative flex-1 px-4 md:px-6 pb-10">
          <section className={`${panel} mt-2`}>
            {/* Card header */}
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-sky-900/30 px-4 py-3">
              <div className="text-[14px] font-semibold text-neutral-100">
                Companies <span className="text-neutral-500 font-normal">({filtered.length})</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="relative">
                  <SearchIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-neutral-500" />
                  <input
                    value={query}
                    onChange={(e) => { setQuery(e.target.value); setPage(1); }}
                    placeholder="Search"
                    className="rounded-md border border-sky-900/30 bg-[#0b1426] pl-8 pr-3 py-1.5 text-[12px] text-neutral-200 placeholder:text-neutral-600 outline-none focus:border-sky-500/50 transition-colors w-48"
                  />
                </div>
                <button type="button" className={ghostBtn}>
                  <Columns3 className="h-3.5 w-3.5" />
                  Columns
                </button>
                <button type="button" className={ghostBtn}>
                  <ListFilter className="h-3.5 w-3.5" />
                  Filters
                </button>
                <button
                  type="button"
                  className="inline-flex items-center justify-center rounded-md border border-sky-900/30 bg-transparent hover:bg-sky-900/30 p-1.5 text-neutral-400 hover:text-sky-300 transition-colors"
                  aria-label="Export"
                >
                  <Download className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
              <table className="w-full border-separate border-spacing-0">
                <thead className="bg-[#0b1426]/60">
                  <tr>
                    <SortHeader k="name">Company</SortHeader>
                    <SortHeader k="sighting">Most Recent Sighting</SortHeader>
                    <SortHeader k="identity">Identity Threat Index</SortHeader>
                    <SortHeader k="malware">Malware Index</SortHeader>
                    <SortHeader k="breach">Breach Index</SortHeader>
                    <SortHeader k="phish">Phish Index</SortHeader>
                    <SortHeader k="combo">Comboleist Index</SortHeader>
                    <SortHeader k="size">Size (Employees)</SortHeader>
                    <SortHeader k="country">Country</SortHeader>
                  </tr>
                </thead>
                <tbody>
                  {pageRows.map((r, i) => (
                    <tr
                      key={r.domain}
                      onClick={() => navigate(`/supply-chain/${encodeURIComponent(r.domain)}`)}
                      className={`border-b border-sky-900/20 hover:bg-sky-900/15 cursor-pointer transition-colors ${i % 2 === 0 ? "bg-transparent" : "bg-[#091020]/40"}`}
                    >
                      <td className="px-4 py-3 border-t border-sky-900/20">
                        <div className="flex items-center gap-2.5">
                          <CompanyAvatar name={r.name} />
                          <div className="leading-tight">
                            <div className="text-[13px] font-medium text-sky-300 hover:text-sky-200 underline-offset-2 hover:underline">
                              {r.name}
                            </div>
                            <div className="text-[11px] text-neutral-500">{r.domain}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 border-t border-sky-900/20 text-[13px] text-neutral-300">
                        {r.sighting}
                      </td>
                      <td className="px-4 py-3 border-t border-sky-900/20">
                        <div className="flex items-center gap-2">
                          <Gauge score={r.identity} />
                          {r.trend === "down" && (
                            <TrendingDown className="h-3.5 w-3.5 text-emerald-400" aria-label="Trending down" />
                          )}
                          {r.trend === "up" && (
                            <TrendingUp className="h-3.5 w-3.5 text-rose-400" aria-label="Trending up" />
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 border-t border-sky-900/20">
                        <ScoreCell v={r.malware.v} d={r.malware.d} />
                      </td>
                      <td className="px-4 py-3 border-t border-sky-900/20">
                        <ScoreCell v={r.breach.v} d={r.breach.d} />
                      </td>
                      <td className="px-4 py-3 border-t border-sky-900/20">
                        <ScoreCell v={r.phish.v} d={r.phish.d} />
                      </td>
                      <td className="px-4 py-3 border-t border-sky-900/20">
                        <ScoreCell v={r.combo.v} d={r.combo.d} />
                      </td>
                      <td className="px-4 py-3 border-t border-sky-900/20 text-[13px] text-neutral-300 tabular-nums">
                        {r.size}
                      </td>
                      <td className="px-4 py-3 border-t border-sky-900/20 text-[18px] leading-none">
                        <span title={r.country}>{FLAG[r.country] || r.country}</span>
                      </td>
                    </tr>
                  ))}
                  {pageRows.length === 0 && (
                    <tr>
                      <td colSpan={9} className="px-4 py-10 text-center text-[13px] text-neutral-500">
                        No companies match your search.
                      </td>
                    </tr>
                  )}
                  {/* Blank filler rows — keep the table the same height on the
                      last/short page so the pagination footer doesn't jump. */}
                  {pageRows.length > 0 &&
                    pageRows.length < pageSize &&
                    Array.from({ length: pageSize - pageRows.length }).map((_, i) => (
                      <tr key={`filler-${i}`} aria-hidden className="pointer-events-none">
                        <td className="px-4 py-3 border-t border-sky-900/20" colSpan={9}>
                          <div className="h-8" />
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>

            {/* Footer / pagination */}
            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-sky-900/30 px-4 py-2.5">
              <div className="text-[12px] text-neutral-500">
                Results: {filtered.length === 0 ? 0 : (page - 1) * pageSize + 1} – {Math.min(page * pageSize, filtered.length)} of {filtered.length}
              </div>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  className="inline-flex items-center justify-center rounded-md border border-sky-900/30 hover:bg-sky-900/30 p-1 text-neutral-400 hover:text-sky-300 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  aria-label="Previous page"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => setPage(p)}
                    className={`min-w-[28px] h-7 rounded-md text-[12px] font-medium transition-colors ${
                      p === page
                        ? "bg-sky-500/20 text-sky-200 border border-sky-500/40"
                        : "text-neutral-400 hover:text-sky-300 hover:bg-sky-900/30 border border-transparent"
                    }`}
                  >
                    {p}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                  className="inline-flex items-center justify-center rounded-md border border-sky-900/30 hover:bg-sky-900/30 p-1 text-neutral-400 hover:text-sky-300 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  aria-label="Next page"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
              <div className="flex items-center gap-2 text-[12px] text-neutral-500">
                <span>Show</span>
                <select
                  value={pageSize}
                  onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
                  className="rounded-md border border-sky-900/30 bg-[#0b1426] px-2 py-1 text-[12px] text-neutral-200 outline-none focus:border-sky-500/50"
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </select>
              </div>
            </div>
          </section>
        </main>
      </div>
    </div>
  );
}
